import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Wand2, Brain, ArrowRight, Bot, Search, Code, Rocket } from 'lucide-react';

export default function HomePage() {
  return (
    <main className="max-w-6xl mx-auto px-4 py-12">
      {/* Hero Section */}
      <section className="text-center mb-24">
        <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
          Best Free Artificial Intelligence App
          <span className="block text-2xl md:text-3xl text-indigo-600 mt-4">
            Your All-in-One AI Toolkit for Content Creation
          </span>
        </h1>

        <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
          Transform your workflow with our suite of free AI tools.
        </p>

        <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
          Generate content, analyze data, and boost your productivity with cutting-edge artificial intelligence.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/best-free-blog-title-generator-tool"
            className="inline-flex items-center gap-2 bg-indigo-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-indigo-700 transition-all duration-200"
          >
            Try Our Blog Title Generator
            <ArrowRight className="w-5 h-5" />
          </Link>

          <a
            href="https://getqualityleadsfast.com/marketing-optin"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#e94e38] text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-[#d03d29] transition-all duration-200"
          >
            Try The Most Legendary Marketing Tool
            <Rocket className="w-5 h-5" />
          </a>
        </div>
      </section>

      {/* Featured Tools */}
      <section className="mb-24">
        <h2 className="text-3xl font-bold text-center mb-12">AI-Powered Tools for Success</h2>
        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-white p-6 rounded-xl shadow-lg">
            <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mb-4">
              <Wand2 className="w-6 h-6 text-indigo-600" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Blog Title Generator</h3>
            <p className="text-gray-600 mb-4">
              Create engaging, SEO-optimized headlines for your content in seconds.
            </p>
            <Link
              to="/best-free-blog-title-generator-tool"
              className="text-indigo-600 font-medium hover:text-indigo-700"
            >
              Try Now →
            </Link>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow-lg">
            <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mb-4">
              <Brain className="w-6 h-6 text-indigo-600" />
            </div>
            <h3 className="text-xl font-semibold mb-2">AI Content Writer</h3>
            <p className="text-gray-600 mb-4">
              Coming soon: Generate high-quality content with advanced AI technology.
            </p>
            <span className="text-gray-400">Coming Soon</span>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow-lg">
            <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mb-4">
              <Bot className="w-6 h-6 text-indigo-600" />
            </div>
            <h3 className="text-xl font-semibold mb-2">AI Chat Assistant</h3>
            <p className="text-gray-600 mb-4">
              Coming soon: Get instant answers and assistance powered by AI.
            </p>
            <span className="text-gray-400">Coming Soon</span>
          </div>
        </div>
      </section>

      {/* SEO Content */}
      <section className="prose prose-lg max-w-4xl mx-auto space-y-12">
        <div>
          <h2>Why Choose Our Best Free Artificial Intelligence App?</h2>
          
          <p>
            Looking to harness the power of AI without breaking the bank?
          </p>

          <p>
            Our Best Free Artificial Intelligence App is your gateway to professional-grade AI tools that deliver real results.
          </p>
        </div>

        <div>
          <h3>AI Made Simple</h3>
          <ul className="space-y-4">
            <li>Generate creative content instantly</li>

            <li>Boost productivity with AI automation</li>

            <li>Access enterprise-grade AI models</li>

            <li>Stay ahead with cutting-edge technology</li>
          </ul>
        </div>

        <div>
          <h3>Built for Everyone</h3>
          
          <p>
            Whether you're a content creator, marketer, or business owner, our AI tools are designed to simplify your workflow.
          </p>

          <p>
            We focus on delivering powerful features that amplify your results.
          </p>
        </div>

        <div className="bg-indigo-50 p-6 rounded-lg">
          <h3 className="text-xl font-semibold mb-4">Why Our AI App Stands Out</h3>
          <ul className="space-y-4">
            <li>✓ No credit card required</li>

            <li>✓ Professional-grade results</li>

            <li>✓ User-friendly interface</li>

            <li>✓ Regular updates and new features</li>
          </ul>
        </div>

        <div>
          <h3>Frequently Asked Questions</h3>
          <div className="space-y-8">
            <div>
              <h4>Is this artificial intelligence app really free?</h4>
              
              <p>
                Yes!
              </p>

              <p>
                Our core AI tools are completely free to use.
              </p>

              <p>
                We believe in making AI accessible to everyone.
              </p>
            </div>

            <div>
              <h4>How advanced is the AI technology?</h4>
              
              <p>
                We use state-of-the-art language models and machine learning algorithms.
              </p>

              <p>
                This ensures you get professional-quality results every time.
              </p>
            </div>

            <div>
              <h4>What makes this the best free AI app?</h4>
              
              <p>
                Our combination of powerful features and user-friendly interface sets us apart.
              </p>

              <p>
                We offer enterprise-level AI capabilities at zero cost.
              </p>

              <p>
                This makes it the perfect choice for anyone looking to leverage AI technology.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gray-900 text-white p-8 rounded-xl">
          <h3 className="text-2xl font-bold mb-4">Ready to Experience the Power of AI?</h3>
          
          <p className="mb-6">
            Start with our most popular tool - the Blog Title Generator.
          </p>

          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/best-free-blog-title-generator-tool"
              className="inline-flex items-center gap-2 bg-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-indigo-700 transition-all duration-200"
            >
              Get Started Now
              <ArrowRight className="w-5 h-5" />
            </Link>

            <a
              href="https://getqualityleadsfast.com/marketing-optin"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#e94e38] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#d03d29] transition-all duration-200"
            >
              Try The Most Legendary Marketing Tool
              <Rocket className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}